'use client';

import { useId } from 'react';
import { MenuInfo } from './MenuInfo';
import {
  resolveIcon,
  externalProps,
  resolveHref,
  trackMenuClick,
} from './menuUtils';
import { Icon } from '@/components/Icon';
import type { EventTrackingResolved } from '@/lib/gtm/pushCmsEventTracking';
import type { MenuItem } from '@/types/menu';

interface MenuAccordionItemProps {
  item: MenuItem;
  menuTracking?: EventTrackingResolved[];
  isExpanded: boolean;
  onToggle: () => void;
  onClose: () => void;
}

export const MenuAccordionItem = ({
  item,
  menuTracking,
  isExpanded,
  onToggle,
  onClose,
}: MenuAccordionItemProps) => {
  const panelId = useId();

  if (!item.children.length) {
    return (
      <a
        href={resolveHref(item.linkTarget)}
        onClick={() => {
          trackMenuClick(item.label, item.tracking, menuTracking);
          onClose();
        }}
        {...externalProps(resolveHref(item.linkTarget))}
        className="hover:text-primary flex items-center gap-3 py-3 text-base font-medium transition-colors md:py-4"
      >
        {item.icon && (
          <Icon
            src={resolveIcon(item.icon)!}
            size={24}
            className="shrink-0 opacity-60"
          />
        )}
        {item.label}
      </a>
    );
  }

  return (
    <>
      <button
        onClick={() => {
          trackMenuClick(item.label, item.tracking, menuTracking);
          onToggle();
        }}
        aria-expanded={isExpanded}
        aria-controls={panelId}
        className="hover:text-primary flex w-full items-center gap-3 py-3 text-base font-medium transition-colors md:py-4"
      >
        {item.icon && (
          <Icon
            src={resolveIcon(item.icon)!}
            size={24}
            className="shrink-0 opacity-60"
          />
        )}
        <span className="flex-1 text-left">{item.label}</span>
        <span className="lg:hidden">
          <Icon
            src={
              isExpanded
                ? '/icons/svg/chevron-up.svg'
                : '/icons/svg/chevron-down.svg'
            }
            size={24}
            className="mr-2"
          />
        </span>
        <span className="hidden lg:block">
          <Icon src="/icons/svg/chevron-right.svg" size={16} />
        </span>
      </button>

      {/*
        Mounted whether or not the item is expanded, and hidden with the
        `hidden` attribute (SH-553). SH-528 had done this for the menu itself,
        but every sub-item stayed behind `isExpanded` — so the second level
        reached a crawler only if it clicked, and this site has no
        always-visible nav at any breakpoint to publish those links instead.

        This is the only copy of the second level in the server HTML: the
        desktop `MenuSubPanel` renders the same children, but only for the
        item a visitor has actually opened.

        `hidden` and not an opacity/height trick, for the reason the nav uses
        it: it takes the subtree out of the accessibility tree *and* the tab
        order, so a collapsed sub-list is not something a keyboard user walks
        through invisibly.
      */}
      <div
        id={panelId}
        hidden={!isExpanded}
        className="-mx-6 bg-white pb-4 lg:hidden"
      >
        <ul className="list-none p-0">
          {item.children.map((child) => (
            <li
              key={`${child.label}-${child.linkTarget}`}
              className="border-card-border border-b last:border-b-0"
            >
              <a
                href={child.linkTarget ?? '#'}
                onClick={() => {
                  trackMenuClick(child.label, child.tracking, menuTracking);
                  onClose();
                }}
                {...externalProps(child.linkTarget)}
                className="hover:text-primary flex w-full items-center gap-3 px-6 py-3 text-base transition-colors md:py-4"
              >
                <span className="w-6 shrink-0">
                  {child.icon && (
                    <Icon
                      src={resolveIcon(child.icon)!}
                      size={24}
                      className="opacity-60"
                    />
                  )}
                </span>
                {child.label}
              </a>
            </li>
          ))}
        </ul>
        {item.cta && (
          <div className="mr-10 ml-6 md:mx-6">
            <MenuInfo cta={item.cta} onClose={onClose} />
          </div>
        )}
      </div>
    </>
  );
};
